import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  IconButton,
  PriceBlock,
  ProductGrid,
  SearchBar,
} from '@/components/ui';
import { Grid2X2, List, Loader2, ShoppingCart } from '@/components/ui/icons';
import { catalogService, isLikelyTcgSetCode, normalizeTcgSetCode } from '@/services';
import { useAuthStore, useCartStore, useSettingsStore } from '@/stores';
import type { Printing, Product } from '@/types';
import { formatPrice } from '@/utils/format';
import { CatalogImage } from '../components/CatalogImage';
import { TcgCardSearchResults } from '../components/TcgCardSearchResults';
import { commonStockCountLabel } from '../utils/stockDisplay';
import { getSuggestionSetCodes, mergePrintingsByIdentity } from '../utils/tcgSearch';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_TCG_SEARCH_LENGTH = 3;
const TCG_SHOP_TAKE = 96;
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300x300?text=No+Image';

type StoreView = 'products' | 'tcg';
type TcgGameView = 'yugioh' | 'other';
type TcgDisplayMode = 'grid' | 'list';

function isAvailableTcgPrinting(printing: Printing): boolean {
  const stock =
    printing.aggregatedStock?.aggregatedStock ??
    printing.listings?.reduce((total, listing) => total + (listing.stock ?? 0), 0) ??
    0;
  const listingCount = printing.aggregatedStock?.listingCount ?? printing.listings?.length ?? 0;

  return printing.availability.isAvailable && stock > 0 && listingCount > 0;
}

export function ProductsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const { t: tc } = useTranslation('catalog');
  const { isAuthenticated } = useAuthStore();
  const currency = useSettingsStore((s) => s.currency);
  const { addProduct } = useCartStore();
  const [addedMessage, setAddedMessage] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  const [addingProductId, setAddingProductId] = useState<string | null>(null);
  const categoryId = searchParams.get('category') ?? undefined;
  const activeView: StoreView = searchParams.get('type') === 'tcg' ? 'tcg' : 'products';
  const activeTcgGame: TcgGameView = searchParams.get('game') === 'other' ? 'other' : 'yugioh';
  const isYuGiOhTcgView = activeView === 'tcg' && activeTcgGame === 'yugioh';
  const tcgDisplayMode: TcgDisplayMode = searchParams.get('view') === 'list' ? 'list' : 'grid';
  const initialQ = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(initialQ);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ.trim());
  const isTcgSearchReady =
    debouncedSearch.length === 0 || debouncedSearch.length >= MIN_TCG_SEARCH_LENGTH;
  const isTcgSetCodeSearch = isLikelyTcgSetCode(debouncedSearch);
  const inputNeedsMoreChars =
    isYuGiOhTcgView &&
    searchInput.trim().length > 0 &&
    searchInput.trim().length < MIN_TCG_SEARCH_LENGTH;

  useEffect(() => {
    setSearchInput(initialQ);
    setDebouncedSearch(initialQ.trim());
  }, [initialQ]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      const next = searchInput.trim();
      setDebouncedSearch(next);
      setSearchParams((previous) => {
        const params = new URLSearchParams(previous);
        if (next) params.set('q', next);
        else params.delete('q');
        return params;
      }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput, setSearchParams]);

  const handleTcgDisplayModeChange = (mode: TcgDisplayMode) => {
    const params = new URLSearchParams(searchParams);
    if (mode === 'list') {
      params.set('view', 'list');
    } else {
      params.delete('view');
    }
    setSearchParams(params);
  };

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products', 'active', currency, debouncedSearch, categoryId],
    queryFn: async () => {
      if (categoryId) {
        const list = await catalogService.getProductsByCategory(categoryId, currency);
        if (!debouncedSearch) return list;
        const q = debouncedSearch.toLowerCase();
        return list.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            (p.description ?? '').toLowerCase().includes(q),
        );
      }
      return catalogService.getActiveProducts({
        currency,
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
      });
    },
    enabled: activeView === 'products',
    refetchInterval: 30 * 1000,
    staleTime: 10 * 1000,
  });

  const {
    data: tcgPrintings = [],
    isLoading: isTcgLoading,
    error: tcgError,
  } = useQuery({
    queryKey: ['products-page-tcg-printings', currency, debouncedSearch],
    queryFn: async () => {
      const params = {
        skip: 0,
        take: TCG_SHOP_TAKE,
        currency,
        availableOnly: true,
      };
      const printings = debouncedSearch
        ? isTcgSetCodeSearch
          ? await catalogService.getPrintings({
              ...params,
              setCode: normalizeTcgSetCode(debouncedSearch),
            })
          : await catalogService.searchPrintings({ ...params, q: debouncedSearch })
        : await catalogService.getPrintings(params);

      return printings.filter(isAvailableTcgPrinting);
    },
    enabled: isYuGiOhTcgView && isTcgSearchReady,
    refetchInterval: 30 * 1000,
    staleTime: 10 * 1000,
  });

  const {
    data: tcgCardSuggestions = [],
    isLoading: isTcgSuggestionsLoading,
    error: tcgSuggestionsError,
  } = useQuery({
    queryKey: ['products-page-tcg-card-suggestions', debouncedSearch, currency],
    queryFn: () =>
      catalogService.searchTcgCardSuggestions({
        q: debouncedSearch,
        take: 10,
        currency,
      }),
    enabled: isYuGiOhTcgView && debouncedSearch.length >= MIN_TCG_SEARCH_LENGTH,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const suggestionSetCodes = useMemo(
    () => getSuggestionSetCodes(tcgCardSuggestions),
    [tcgCardSuggestions],
  );

  const {
    data: associatedTcgPrintings = [],
    isLoading: isAssociatedTcgLoading,
    error: associatedTcgError,
  } = useQuery({
    queryKey: ['products-page-tcg-associated-printings', currency, suggestionSetCodes],
    queryFn: () =>
      catalogService.getPrintingsBySetCodes(suggestionSetCodes, {
        currency,
        takePerCode: 8,
        availableOnly: true,
      }),
    enabled: isYuGiOhTcgView && suggestionSetCodes.length > 0,
    refetchInterval: 30 * 1000,
    staleTime: 10 * 1000,
  });

  const mergedTcgPrintings = useMemo(
    () => mergePrintingsByIdentity(tcgPrintings, associatedTcgPrintings),
    [associatedTcgPrintings, tcgPrintings],
  );

  const visibleTcgPrintings = useMemo(
    () =>
      mergedTcgPrintings.filter(isAvailableTcgPrinting),
    [debouncedSearch, mergedTcgPrintings],
  );

  const handleAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (addingProductId) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/products' } });
      return;
    }
    if (!product?.id) return;
    setAddingProductId(product.id);
    try {
      await addProduct(product.id, 1);
      setAddedMessage(true);
      setTimeout(() => setAddedMessage(false), 2500);
    } catch (err) {
      console.error('Failed to add to cart:', err);
      setCartError(err instanceof Error ? err.message : 'Error al añadir al carrito');
      setTimeout(() => setCartError(null), 4000);
    } finally {
      setAddingProductId(null);
    }
  };

  const storeHeader = (
    <div className="mb-5 space-y-4">
      <div>
        <div>
          <h1 className="font-heading text-heading-xl text-brand-950">
            {activeView === 'tcg' ? t('store.tcgTitle') : t('store.title')}
          </h1>
          <p className="mt-1 max-w-2xl font-sans text-sm text-brand-700">
            {activeView === 'tcg' ? t('store.tcgSubtitle') : t('store.productsSubtitle')}
          </p>
        </div>
      </div>

      {activeView === 'tcg' ? (
        <div className="flex flex-col gap-3 rounded-lg border border-brand-700/15 bg-surface px-3 py-2.5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={activeTcgGame === 'yugioh' ? 'brand' : 'neutral'}>
                {activeTcgGame === 'yugioh' ? t('store.yugiohTab') : t('store.otherTcgTab')}
              </Badge>
              {isTcgLoading || isTcgSuggestionsLoading || isAssociatedTcgLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-brand-700" aria-hidden />
              ) : null}
              {activeTcgGame !== 'yugioh' ? (
                <span className="font-sans text-xs font-semibold uppercase text-brand-700">
                  {t('store.otherTcgBadge')}
                </span>
              ) : null}
            </div>
            {activeTcgGame !== 'yugioh' ? (
              <p className="mt-1 font-sans text-xs text-brand-700">
                {t('store.otherTcgDescription')}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {activeTcgGame === 'yugioh' ? (
              <div
                className="inline-flex rounded-md border border-brand-700/20 bg-brand-100 p-1"
                role="group"
                aria-label={t('store.viewModeAria')}
              >
                <IconButton
                  type="button"
                  variant={tcgDisplayMode === 'grid' ? 'outline' : 'ghost'}
                  size="sm"
                  aria-label={t('store.gridView')}
                  title={t('store.gridView')}
                  onClick={() => handleTcgDisplayModeChange('grid')}
                  className={tcgDisplayMode === 'grid' ? 'bg-brand-400 text-brand-950' : undefined}
                >
                  <Grid2X2 className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  type="button"
                  variant={tcgDisplayMode === 'list' ? 'outline' : 'ghost'}
                  size="sm"
                  aria-label={t('store.listView')}
                  title={t('store.listView')}
                  onClick={() => handleTcgDisplayModeChange('list')}
                  className={tcgDisplayMode === 'list' ? 'bg-brand-400 text-brand-950' : undefined}
                >
                  <List className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  const searchBar = (
    <div className="mb-6">
      <SearchBar
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder={
          activeView === 'tcg'
            ? t('store.tcgSearchPlaceholder')
            : t('common.searchProductsPlaceholder')
        }
        aria-label={t('common.search')}
      />
      {inputNeedsMoreChars ? (
        <p className="mt-1 font-sans text-sm text-brand-700">{tc('list.search.hintMinChars')}</p>
      ) : null}
      {activeView === 'tcg' && isTcgSuggestionsLoading ? (
        <p className="mt-1 font-sans text-sm text-brand-700">
          {tc('list.search.loadingSuggestions')}
        </p>
      ) : null}
      {activeView === 'tcg' && isAssociatedTcgLoading ? (
        <p className="mt-1 font-sans text-sm text-brand-700">
          {tc('offers.searchingAssociated')}
        </p>
      ) : null}
      {activeView === 'tcg' && tcgSuggestionsError ? (
        <p className="mt-1 font-sans text-sm text-amber-800">
          {tc('list.search.suggestionsError')}
        </p>
      ) : null}
    </div>
  );

  const renderProducts = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="inline-flex items-center gap-2 text-lg text-brand-700">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            {t('common.loadingProducts')}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-lg text-red-600">{t('common.loadProductsError')}</div>
        </div>
      );
    }

    if (!products || products.length === 0) {
      return (
        <div className="flex min-h-[300px] items-center justify-center">
          <p className="text-lg text-brand-700">
            {debouncedSearch ? t('common.noSearchResults') : t('common.noProductsAvailable')}
          </p>
        </div>
      );
    }

    return (
      <ProductGrid className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((product) => {
          const isInStock = (product.stock ?? 0) > 0;
          const isAddingThisProduct = addingProductId === product.id;
          const imageUrl =
            product.images && product.images.length > 0
              ? product.images[0].url
              : PLACEHOLDER_IMAGE;

          return (
            <article
              key={product.id}
              onClick={() => navigate(`/products/${product.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/products/${product.id}`);
                }
              }}
              role="button"
              tabIndex={0}
              className="group flex h-full min-h-[18rem] cursor-pointer flex-col overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <div className="flex aspect-square w-full items-center justify-center overflow-hidden bg-brand-100">
                <CatalogImage
                  src={imageUrl}
                  alt={product.name}
                  className="h-full w-full object-contain transition-transform group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5 p-2.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={isInStock ? 'success' : 'danger'}>
                    {isInStock ? commonStockCountLabel(t, product.stock ?? 0) : t('common.outOfStock')}
                  </Badge>
                  {product.category?.name ? (
                    <Badge variant="neutral">{product.category.name}</Badge>
                  ) : null}
                </div>
                <div className="min-w-0">
                  <h2 className="line-clamp-2 font-heading text-sm font-semibold text-brand-950">
                    {product.name}
                  </h2>
                  {product.description ? (
                    <p className="mt-0.5 line-clamp-2 font-sans text-xs text-brand-700">
                      {product.description}
                    </p>
                  ) : null}
                </div>
                <div className="mt-auto flex items-end justify-between gap-3">
                  <PriceBlock
                    amount={formatPrice(product.price, product.currency)}
                    size="sm"
                    secondary={
                      product.originalPrice != null && product.originalCurrency
                        ? `≈ ${formatPrice(product.originalPrice, product.originalCurrency)}`
                        : t('common.stockCount', { count: product.stock ?? 0 })
                    }
                  />
                  <IconButton
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!isInStock || isAddingThisProduct}
                    aria-label={t('common.addToCart')}
                    title={t('common.addToCart')}
                    onClick={(event) => handleAddToCart(product, event)}
                    className="rounded-full bg-brand-400 text-brand-950 hover:bg-brand-600"
                  >
                    {isAddingThisProduct ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <ShoppingCart className="h-4 w-4" aria-hidden />
                    )}
                  </IconButton>
                </div>
              </div>
            </article>
          );
        })}
      </ProductGrid>
    );
  };

  const renderTcgShop = () => {
    const hasTcgResults = visibleTcgPrintings.length > 0;

    if (activeTcgGame === 'other') {
      return (
        <div className="rounded-lg border border-brand-700/15 bg-surface px-5 py-8 text-center shadow-sm">
          <Badge variant="neutral">{t('store.otherTcgBadge')}</Badge>
          <h2 className="mt-3 font-heading text-heading-md text-brand-950">
            {t('store.otherTcgTitle')}
          </h2>
          <p className="mx-auto mt-2 max-w-xl font-sans text-sm text-brand-700">
            {t('store.otherTcgDescription')}
          </p>
        </div>
      );
    }

    if (!isTcgSearchReady) {
      return (
        <div className="flex min-h-[300px] items-center justify-center">
          <p className="text-lg text-brand-700">{tc('list.search.hintMinChars')}</p>
        </div>
      );
    }

    if (isTcgLoading && !hasTcgResults) {
      return (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="inline-flex items-center gap-2 text-lg text-brand-700">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            {t('store.tcgLoading')}
          </div>
        </div>
      );
    }

    if ((tcgError || associatedTcgError) && !hasTcgResults) {
      return (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-lg text-red-600">{t('store.tcgLoadError')}</div>
        </div>
      );
    }

    if (!hasTcgResults) {
      return (
        <div className="flex min-h-[300px] items-center justify-center">
          <p className="text-lg text-brand-700">
            {debouncedSearch ? t('store.tcgNoSearchResults') : t('store.tcgEmpty')}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {tcgError || associatedTcgError ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 font-sans text-sm text-amber-900">
            {t('store.tcgPartialLoadError')}
          </div>
        ) : null}
        <TcgCardSearchResults
          printings={visibleTcgPrintings}
          suggestions={[]}
          query={debouncedSearch}
          displayMode={tcgDisplayMode}
        />
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {addedMessage ? (
        <div
          className="fixed right-4 top-4 z-50 animate-fade-in rounded-lg bg-green-600 px-4 py-2 text-white shadow-lg"
          role="status"
        >
          {t('common.addedToCart')}
        </div>
      ) : null}
      {cartError ? (
        <div
          className="fixed right-4 top-4 z-50 animate-fade-in rounded-lg bg-red-600 px-4 py-2 text-white shadow-lg"
          role="alert"
        >
          {cartError}
        </div>
      ) : null}

      {storeHeader}
      {activeView === 'products' || isYuGiOhTcgView ? searchBar : null}

      {activeView === 'products' ? renderProducts() : renderTcgShop()}
    </div>
  );
}
